import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Package, Plus, Search, TrendingUp, TrendingDown, ArrowUpDown,
  Box, History, Minus, AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FranchiseInventory = ({ franchiseId, authToken }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeView, setActiveView] = useState('stock'); // stock | logs

  // Stock update dialog
  const [isStockUpdateOpen, setIsStockUpdateOpen] = useState(false);
  const [stockUpdateItem, setStockUpdateItem] = useState(null);
  const [stockUpdateForm, setStockUpdateForm] = useState({ type: 'stock_in', quantity: '', notes: '' });

  // Add item dialog
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ item_name: '', category: 'Raw Materials', unit: '', base_unit: 'kg', conversion_factor: 1, cost_price: 0, initial_stock: 0 });

  // Logs
  const [logs, setLogs] = useState([]);

  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchInventory = useCallback(async () => {
    if (!franchiseId) return;
    setIsLoading(true);
    try {
      const params = selectedCategory !== 'all' ? `?category=${encodeURIComponent(selectedCategory)}` : '';
      const response = await axios.get(`${API}/inventory/franchise/${franchiseId}${params}`);
      setItems(response.data.items || []);
      setCategories(response.data.categories || []);
      setCanManage(response.data.can_manage_inventory || false);
    } catch (error) {
      toast.error('Failed to load inventory');
    }
    setIsLoading(false);
  }, [franchiseId, selectedCategory]);

  const fetchLogs = useCallback(async () => {
    if (!franchiseId) return;
    try {
      const response = await axios.get(`${API}/inventory/logs/${franchiseId}?limit=50`);
      setLogs(response.data.logs || []);
    } catch (error) {
      toast.error('Failed to load activity logs');
    }
  }, [franchiseId]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  useEffect(() => {
    if (activeView === 'logs') fetchLogs();
  }, [activeView, fetchLogs]);

  const handleStockUpdate = async (e) => {
    e.preventDefault();
    if (!stockUpdateItem) return;
    try {
      const qty = parseFloat(stockUpdateForm.quantity);
      if (isNaN(qty) || qty <= 0) {
        toast.error('Enter a valid quantity');
        return;
      }
      await axios.post(`${API}/inventory/stock-update`, {
        franchise_id: franchiseId,
        item_id: stockUpdateItem.item_id,
        type: stockUpdateForm.type,
        quantity: qty,
        notes: stockUpdateForm.notes
      }, { headers });
      toast.success(`Stock ${stockUpdateForm.type === 'stock_in' ? 'increased' : 'decreased'} successfully`);
      setIsStockUpdateOpen(false);
      setStockUpdateForm({ type: 'stock_in', quantity: '', notes: '' });
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stock');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inventory/franchise-add-item`, addForm, { headers });
      toast.success('Item added to inventory');
      setIsAddOpen(false);
      setAddForm({ item_name: '', category: 'Raw Materials', unit: '', base_unit: 'kg', conversion_factor: 1, cost_price: 0, initial_stock: 0 });
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const openStockUpdate = (item) => {
    setStockUpdateItem(item);
    setStockUpdateForm({ type: 'stock_in', quantity: '', notes: '' });
    setIsStockUpdateOpen(true);
  };

  const filteredItems = items.filter(item =>
    item.item_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalValue = items.reduce((sum, item) => sum + (item.current_stock || 0), 0);
  const negativeCount = items.filter(i => (i.current_stock || 0) < 0).length;

  const BASE_UNITS = ['kg', 'g', 'L', 'ml', 'pieces', 'packets', 'rolls', 'boxes'];

  const getLogIcon = (type) => {
    switch (type) {
      case 'stock_in': case 'rm_order_received': return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'stock_out': case 'auto_deduction': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <ArrowUpDown className="w-4 h-4 text-blue-500" />;
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'stock_in': case 'rm_order_received': return 'text-green-600';
      case 'stock_out': case 'auto_deduction': return 'text-red-500';
      default: return 'text-blue-500';
    }
  };

  return (
    <div data-testid="franchise-inventory-section" className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-primary">Inventory</h2>
        <div className="flex gap-2">
          <Button variant={activeView === 'stock' ? 'default' : 'outline'} size="sm" onClick={() => setActiveView('stock')} data-testid="inv-stock-tab" className="rounded-lg">
            <Box className="w-4 h-4 mr-1" /> Stock
          </Button>
          <Button variant={activeView === 'logs' ? 'default' : 'outline'} size="sm" onClick={() => setActiveView('logs')} data-testid="inv-logs-tab" className="rounded-lg">
            <History className="w-4 h-4 mr-1" /> Logs
          </Button>
        </div>
      </div>

      {activeView === 'stock' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="border-border"><CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Items</div>
              <div className="text-xl font-bold text-primary">{items.length}</div>
            </CardContent></Card>
            <Card className="border-border"><CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Categories</div>
              <div className="text-xl font-bold text-primary">{categories.length}</div>
            </CardContent></Card>
            <Card className="border-border"><CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Negative</div>
              <div className={`text-xl font-bold ${negativeCount > 0 ? 'text-red-500' : 'text-green-600'}`}>{negativeCount}</div>
            </CardContent></Card>
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input data-testid="franchise-inv-search" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9 rounded-xl text-sm" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="franchise-inv-category" className="w-36 h-9 rounded-xl text-sm"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {canManage && (
              <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="franchise-add-inv-btn" size="sm" className="h-9 rounded-xl"><Plus className="w-4 h-4" /></Button>
                </DialogTrigger>
                <DialogContent className="bg-surface">
                  <DialogHeader><DialogTitle>Add Inventory Item</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddItem} className="space-y-3 mt-2">
                    <div>
                      <Label>Item Name *</Label>
                      <Input value={addForm.item_name} onChange={(e) => setAddForm({...addForm, item_name: e.target.value})} className="h-9 rounded-xl" required />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Category</Label>
                        <Select value={addForm.category} onValueChange={(v) => setAddForm({...addForm, category: v})}>
                          <SelectTrigger className="h-9 rounded-xl"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                            <SelectItem value="Finished Goods">Finished Goods</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Unit *</Label>
                        <Input value={addForm.unit} onChange={(e) => setAddForm({...addForm, unit: e.target.value})} placeholder="e.g., Kg" className="h-9 rounded-xl" required />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>Base Unit</Label>
                        <Select value={addForm.base_unit} onValueChange={(v) => setAddForm({...addForm, base_unit: v})}>
                          <SelectTrigger className="h-9 rounded-xl"><SelectValue /></SelectTrigger>
                          <SelectContent>{BASE_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Factor</Label>
                        <Input type="number" step="0.01" value={addForm.conversion_factor} onChange={(e) => setAddForm({...addForm, conversion_factor: parseFloat(e.target.value) || 1})} className="h-9 rounded-xl" />
                      </div>
                      <div>
                        <Label>Initial Stock</Label>
                        <Input type="number" step="0.01" value={addForm.initial_stock} onChange={(e) => setAddForm({...addForm, initial_stock: parseFloat(e.target.value) || 0})} className="h-9 rounded-xl" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-9 rounded-xl font-bold">Add Item</Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Stock List */}
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No inventory items</div>
          ) : (
            <div className="space-y-2">
              {filteredItems.map(item => (
                <Card key={item.item_id} data-testid={`franchise-inv-item-${item.item_id}`}
                  className={`border-border ${item.current_stock < 0 ? 'border-red-200 bg-red-50/50' : ''}`}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{item.item_name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-xs py-0">{item.category}</Badge>
                        <span className="text-xs text-muted-foreground">{item.base_unit}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className={`text-lg font-bold ${item.current_stock < 0 ? 'text-red-600' : 'text-primary'}`}>
                          {item.current_stock}
                        </div>
                        <div className="text-xs text-muted-foreground">{item.base_unit}</div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => openStockUpdate(item)} data-testid={`stock-update-btn-${item.item_id}`} className="h-8 px-2 rounded-lg">
                        <ArrowUpDown className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Stock Update Dialog */}
          <Dialog open={isStockUpdateOpen} onOpenChange={setIsStockUpdateOpen}>
            <DialogContent className="bg-surface">
              <DialogHeader>
                <DialogTitle>Update Stock — {stockUpdateItem?.item_name}</DialogTitle>
              </DialogHeader>
              <div className="text-sm text-muted-foreground mb-2">
                Current: <span className="font-bold text-foreground">{stockUpdateItem?.current_stock} {stockUpdateItem?.base_unit}</span>
              </div>
              <form onSubmit={handleStockUpdate} className="space-y-3">
                <div className="flex gap-2">
                  <Button type="button" variant={stockUpdateForm.type === 'stock_in' ? 'default' : 'outline'}
                    onClick={() => setStockUpdateForm({...stockUpdateForm, type: 'stock_in'})}
                    className="flex-1 h-10 rounded-xl" data-testid="stock-in-btn">
                    <TrendingUp className="w-4 h-4 mr-1" /> Stock In
                  </Button>
                  <Button type="button" variant={stockUpdateForm.type === 'stock_out' ? 'default' : 'outline'}
                    onClick={() => setStockUpdateForm({...stockUpdateForm, type: 'stock_out'})}
                    className="flex-1 h-10 rounded-xl" data-testid="stock-out-btn">
                    <TrendingDown className="w-4 h-4 mr-1" /> Stock Out
                  </Button>
                </div>
                <div>
                  <Label>Quantity ({stockUpdateItem?.base_unit})</Label>
                  <Input data-testid="stock-qty-input" type="number" step="0.01" min="0.01" value={stockUpdateForm.quantity}
                    onChange={(e) => setStockUpdateForm({...stockUpdateForm, quantity: e.target.value})} className="h-10 rounded-xl" required autoFocus />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input data-testid="stock-notes-input" value={stockUpdateForm.notes}
                    onChange={(e) => setStockUpdateForm({...stockUpdateForm, notes: e.target.value})} placeholder="e.g., Purchase, Wastage, Expired..." className="h-10 rounded-xl" />
                </div>
                <Button type="submit" data-testid="stock-update-submit" className="w-full h-10 rounded-xl font-bold">
                  Update Stock
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </>
      )}

      {activeView === 'logs' && (
        <div className="space-y-2">
          {logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No activity logs yet</div>
          ) : (
            logs.map(log => (
              <Card key={log.log_id} className="border-border" data-testid={`inv-log-${log.log_id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    {getLogIcon(log.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{log.item_name}</span>
                        <Badge variant="outline" className="text-xs py-0">{log.type.replace(/_/g, ' ')}</Badge>
                      </div>
                      <div className="text-sm mt-0.5">
                        <span className="text-muted-foreground">{log.previous_stock}</span>
                        <span className="mx-1">→</span>
                        <span className={getLogColor(log.type) + ' font-medium'}>{log.new_stock}</span>
                        <span className={`ml-1 ${getLogColor(log.type)}`}>
                          ({log.type === 'stock_out' || log.type === 'auto_deduction' ? '-' : '+'}{log.quantity})
                        </span>
                      </div>
                      {log.notes && <div className="text-xs text-muted-foreground mt-0.5">{log.notes}</div>}
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {new Date(log.created_at).toLocaleString()} by {log.created_by}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FranchiseInventory;
